import type { SocialCaptureResponse } from "contracts/api/social/admin";
import type {
  SocialReportProfile,
  SocialReportProfileRollup,
} from "contracts/api/social/report";
import type {
  SocialFindingSeverity,
  SocialJudgment,
} from "contracts/enums/social";
import {
  SOCIAL_ANALYSIS_STATUS,
  SOCIAL_FINDING_SEVERITIES,
  SOCIAL_JUDGMENT,
  SOCIAL_JUDGMENTS,
} from "contracts/enums/social";
import {
  POST_ANALYSIS_STATE,
  type PostAnalysisStateKind,
} from "../../enums/social-report";

type VerdictTally = Record<SocialJudgment, number>;

/**
 * How far one post's analysis got, decided from the latest attempt's status —
 * never from wording. A completed analysis with zero findings is its own
 * deliberate state (`clean`), not an accidental empty list.
 */
export function postAnalysisState(
  capture: SocialCaptureResponse,
): PostAnalysisStateKind {
  const analysis = capture.analysis;
  if (!analysis || analysis.status === SOCIAL_ANALYSIS_STATUS.QUEUED) {
    return POST_ANALYSIS_STATE.PENDING;
  }
  if (analysis.status === SOCIAL_ANALYSIS_STATUS.RUNNING) {
    return POST_ANALYSIS_STATE.RUNNING;
  }
  if (analysis.status === SOCIAL_ANALYSIS_STATUS.FAILED) {
    return POST_ANALYSIS_STATE.FAILED;
  }
  return analysis.findings.length === 0
    ? POST_ANALYSIS_STATE.CLEAN
    : POST_ANALYSIS_STATE.FLAGGED;
}

export function emptyTally(): VerdictTally {
  return Object.fromEntries(
    SOCIAL_JUDGMENTS.map((judgment) => [judgment, 0]),
  ) as VerdictTally;
}

/** Verdict counts summed across every standard of one profile's rollup. */
export function tallyVerdicts(rollup: SocialReportProfileRollup): VerdictTally {
  const tally = emptyTally();
  for (const entry of rollup.standards) {
    for (const judgment of SOCIAL_JUDGMENTS) {
      tally[judgment] += entry.verdicts[judgment];
    }
  }
  return tally;
}

/**
 * The headline numbers for "how is this doing overall". `verified` counts only
 * checks the evidence could settle (pass + fail): an unverifiable or
 * not-applicable check leaves the denominator entirely rather than counting
 * as a failure, and both excluded counts are carried so the reader sees them.
 */
export interface VerifiedChecks {
  passed: number;
  verified: number;
  unableToVerify: number;
  notApplicable: number;
}

export function verifiedChecks(tally: VerdictTally): VerifiedChecks {
  return {
    passed: tally[SOCIAL_JUDGMENT.PASS],
    verified: tally[SOCIAL_JUDGMENT.PASS] + tally[SOCIAL_JUDGMENT.FAIL],
    unableToVerify: tally[SOCIAL_JUDGMENT.UNABLE_TO_VERIFY],
    notApplicable: tally[SOCIAL_JUDGMENT.NOT_APPLICABLE],
  };
}

type ProfileHeadline =
  | { kind: "no-posts" }
  | { kind: "awaiting-analysis"; capturedPosts: number }
  | {
      kind: "scored";
      checks: VerifiedChecks;
      findings: Record<SocialFindingSeverity, number>;
      analysedPosts: number;
      capturedPosts: number;
    };

export function profileHeadline(profile: SocialReportProfile): ProfileHeadline {
  const rollup = profile.rollup;
  if (rollup.capturedPosts === 0) return { kind: "no-posts" };
  if (rollup.analysedPosts === 0) {
    return { kind: "awaiting-analysis", capturedPosts: rollup.capturedPosts };
  }
  return {
    kind: "scored",
    checks: verifiedChecks(tallyVerdicts(rollup)),
    findings: rollup.findings,
    analysedPosts: rollup.analysedPosts,
    capturedPosts: rollup.capturedPosts,
  };
}

interface ClientSummary {
  profiles: number;
  capturedPosts: number;
  analysedPosts: number;
  checks: VerifiedChecks;
  findings: Record<SocialFindingSeverity, number>;
}

/** The whole-client rollup rendered above the profile sections. */
export function clientSummary(profiles: SocialReportProfile[]): ClientSummary {
  const tally = emptyTally();
  const findings = Object.fromEntries(
    SOCIAL_FINDING_SEVERITIES.map((severity) => [severity, 0]),
  ) as Record<SocialFindingSeverity, number>;
  let capturedPosts = 0;
  let analysedPosts = 0;
  for (const profile of profiles) {
    capturedPosts += profile.rollup.capturedPosts;
    analysedPosts += profile.rollup.analysedPosts;
    const profileTally = tallyVerdicts(profile.rollup);
    for (const judgment of SOCIAL_JUDGMENTS) {
      tally[judgment] += profileTally[judgment];
    }
    for (const severity of SOCIAL_FINDING_SEVERITIES) {
      findings[severity] += profile.rollup.findings[severity];
    }
  }
  return {
    profiles: profiles.length,
    capturedPosts,
    analysedPosts,
    checks: verifiedChecks(tally),
    findings,
  };
}

export function totalFindings(
  findings: Record<SocialFindingSeverity, number>,
): number {
  return SOCIAL_FINDING_SEVERITIES.reduce(
    (sum, severity) => sum + findings[severity],
    0,
  );
}

type AnalysisEvidence = NonNullable<
  SocialCaptureResponse["analysis"]
>["evidence"][number];

type CitedEvidence = { key: string; evidence: AnalysisEvidence | null };

/**
 * Resolves cited evidence keys against the attempt's own catalog. A key the
 * catalog does not hold is kept and surfaced as unresolved — the citation is
 * part of the record and must not silently vanish.
 */
export function citedEvidence(
  keys: string[],
  catalog: AnalysisEvidence[],
): CitedEvidence[] {
  const byKey = new Map(catalog.map((entry) => [entry.key, entry]));
  return keys.map((key) => ({ key, evidence: byKey.get(key) ?? null }));
}
